import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getLanguageFromCountry } from '@/i18n'

interface UseCountryLanguageOptions {
  countryCode?: string
  enabled?: boolean
}

export function useCountryLanguage({ countryCode, enabled = true }: UseCountryLanguageOptions) {
  const { i18n } = useTranslation()
  const [originalLanguage, setOriginalLanguage] = useState<string | null>(null)
  const [isLanguageSwitched, setIsLanguageSwitched] = useState(false)
  
  useEffect(() => {
    if (!enabled || !countryCode) {
      return
    }
    
    // Get the appropriate language for the blocked country
    const countryLanguage = getLanguageFromCountry(countryCode)
    
    // Only switch if the country language is different from current language
    if (countryLanguage !== i18n.language) {
      // Store the original language
      setOriginalLanguage(i18n.language)
      
      // Switch to country language
      i18n.changeLanguage(countryLanguage).then(() => {
        setIsLanguageSwitched(true)
      }).catch((error) => {
        console.error('Failed to switch language for country blocking modal:', error)
      })
      
      // Cleanup function to restore original language
      return () => {
        if (originalLanguage) {
          i18n.changeLanguage(originalLanguage).catch((error) => {
            console.error('Failed to restore original language:', error)
          })
          setOriginalLanguage(null)
          setIsLanguageSwitched(false)
        }
      }
    }
  }, [countryCode, enabled, i18n, originalLanguage])
  
  // Also provide manual restore function
  const restoreLanguage = () => {
    if (originalLanguage && isLanguageSwitched) {
      i18n.changeLanguage(originalLanguage).then(() => {
        setOriginalLanguage(null)
        setIsLanguageSwitched(false)
      }).catch((error) => {
        console.error('Failed to restore original language:', error)
      })
    }
  }
  
  return {
    isLanguageSwitched,
    originalLanguage,
    currentLanguage: i18n.language,
    countryLanguage: countryCode ? getLanguageFromCountry(countryCode) : null,
    restoreLanguage
  }
}